import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Drawer, IconButton, MenuItem, Paper, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { ApprovalService, ApprovalTab } from '../../services/approval.service';
import { PERMISSIONS } from '../../constants/permissions';
import {
  ApprovalRequestDetail, ApprovalRequestRow, ApprovalRequestStatus, ApprovalStepStatus,
} from '../../interfaces/approval.interface';
import { formatDate } from '../../utils/dateUtils';

/** Cash transaction subjects (F11d / FW-3B): the summary's party is the category account. */
const CASH_SUBJECTS = new Set(['CASH_IN', 'CASH_OUT']);

const STATUS_COLOR: Record<ApprovalRequestStatus, 'warning' | 'success' | 'error' | 'default'> = {
  PENDING: 'warning', APPROVED: 'success', REJECTED: 'error', CANCELLED: 'default',
};
const STEP_COLOR: Record<ApprovalStepStatus, 'warning' | 'success' | 'error' | 'default'> = {
  WAITING: 'warning', APPROVED: 'success', REJECTED: 'error', SKIPPED: 'default',
};

const money = (v?: number | null) =>
  v == null ? '—' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Approvals inbox: what waits for me, what I submitted, what I decided, and (with APPROVAL_VIEW_ALL) everything. */
const ApprovalsInbox: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canViewAll = hasPermission(PERMISSIONS.APPROVAL_VIEW_ALL);
  const institutionId = AuthService.getSelectedInstitution()!;
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<ApprovalTab>('mine');
  const [rows, setRows] = useState<ApprovalRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [subjectType, setSubjectType] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [detail, setDetail] = useState<ApprovalRequestDetail | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await ApprovalService.list(tab, institutionId, {
        status: (status || undefined) as ApprovalRequestStatus | undefined,
        subjectType: subjectType || undefined,
        search, from: from || undefined, to: to || undefined,
      }));
    } catch (e) {
      toast.showApiError(e, 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, institutionId, status, subjectType, search, from, to]);

  useEffect(() => { load(); }, [load]);

  const open = useCallback(async (id: string) => {
    try {
      setComment('');
      setDetail(await ApprovalService.get(id));
    } catch (e) {
      toast.showApiError(e, 'Failed to open the request');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // deep link from a notification: /approvals?request=<id>
  const requested = params.get('request');
  useEffect(() => { if (requested) open(requested); }, [requested, open]);

  const close = () => {
    setDetail(null);
    if (params.get('request')) { params.delete('request'); setParams(params, { replace: true }); }
  };

  const decide = async (approve: boolean) => {
    if (!detail) return;
    if (!approve && !comment.trim()) { toast.showError('A comment is required to reject'); return; }
    try {
      setBusy(true);
      const seq = detail.request.currentSeq;
      const next = approve
        ? await ApprovalService.approve(detail.request.id, comment, seq)
        : await ApprovalService.reject(detail.request.id, comment, seq);
      toast.showSuccess(approve ? 'Approved' : 'Rejected');
      setDetail(next); setComment('');
      await load();
    } catch (e) {
      toast.showApiError(e, approve ? 'Failed to approve' : 'Failed to reject');
    } finally {
      setBusy(false);
    }
  };

  const adminCancel = async () => {   // FW-3B
    if (!detail) return;
    if (!window.confirm('Cancel this request? The document goes back to its maker to fix and resubmit; a held bill or cash-out stays held.')) return;
    try {
      setBusy(true);
      setDetail(await ApprovalService.adminCancel(detail.request.id));
      toast.showSuccess('Request cancelled');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to cancel the request');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!detail) return;
    try {
      setBusy(true);
      setDetail(await ApprovalService.cancel(detail.request.id));
      toast.showSuccess('Request withdrawn');
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to withdraw the request');
    } finally {
      setBusy(false);
    }
  };

  const subjectTypes = Array.from(new Set(rows.map(r => r.subjectType)));
  // Polish P2-1: a cash transaction's summary "party" is its category account, so it is labelled as such.
  const anyCash = rows.some(r => CASH_SUBJECTS.has(r.subjectType));
  const partyHeader = anyCash ? (rows.every(r => CASH_SUBJECTS.has(r.subjectType)) ? 'Category' : 'Party / category') : 'Party';

  return (
    <Box sx={{ p: 3 }} data-help-page="approvals.inbox.page">
      <Typography variant="h5">Approvals</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Documents sent for approval. Approving never posts anything: it only lets the person who prepared the document go on.
      </Typography>
      <Tabs data-help-section="approvals.inbox.tabs" value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="mine" label="Waiting for me" />
        <Tab value="submitted" label="Submitted by me" />
        <Tab value="decided" label="Decided by me" />
        {canViewAll && <Tab value="all" label="All" />}
      </Tabs>
      <Box data-help-section="approvals.inbox.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField data-help="approvals.inbox.filterType" select size="small" label="Type" value={subjectType} onChange={e => setSubjectType(e.target.value)}
          sx={{ flex: '1 1 180px', maxWidth: 240 }}>
          <MenuItem value="">All</MenuItem>
          {subjectTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <TextField data-help="approvals.inbox.filterStatus" select size="small" label="Status" value={status} onChange={e => setStatus(e.target.value)}
          sx={{ flex: '1 1 160px', maxWidth: 220 }}>
          <MenuItem value="">All</MenuItem>
          {(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField data-help="approvals.inbox.filterFrom" size="small" type="date" label="From" value={from} onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }} />
        <TextField data-help="approvals.inbox.filterTo" size="small" type="date" label="To" value={to} onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 150px', maxWidth: 200 }} />
        <TextField data-help="approvals.inbox.search" size="small" label="Search" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: '1 1 220px', maxWidth: 320 }} />
      </Box>

      {loading ? <Box sx={{ p: 3 }}><CircularProgress /></Box> : rows.length === 0 ? (
        <Alert severity="info">Nothing here.</Alert>
      ) : (
        <Paper variant="outlined" data-help-section="approvals.inbox.table">
          <TableContainer>
            <Table size="small" data-testid="approvals-table">
              <TableHead>
                <TableRow>
                  <TableCell data-help="approvals.inbox.col.document">Document</TableCell><TableCell data-help="approvals.inbox.col.party">{partyHeader}</TableCell><TableCell align="right" data-help="approvals.inbox.col.amount">Amount</TableCell>
                  <TableCell data-help="approvals.inbox.col.submittedBy">Submitted by</TableCell><TableCell data-help="approvals.inbox.col.submitted">Submitted</TableCell><TableCell data-help="approvals.inbox.col.step">Step</TableCell><TableCell data-help="approvals.inbox.col.status">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => open(r.id)} data-testid={`approval-row-${r.id}`}>
                    <TableCell sx={{ fontWeight: 600 }}>{r.subjectLabel} {r.subjectNumber ?? ''}</TableCell>
                    <TableCell>{r.party ?? '—'}</TableCell>
                    <TableCell align="right">{money(r.amount)}</TableCell>
                    <TableCell>{r.submittedByName ?? '—'}</TableCell>
                    <TableCell>{formatDate(r.submittedAt)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal' }}>
                      {r.status === 'PENDING' ? `${r.currentSeq} of ${r.totalSteps}${r.waitingFor.length ? ` · ${r.waitingFor.join(', ')}` : ''}` : '—'}
                    </TableCell>
                    <TableCell><Chip size="small" color={STATUS_COLOR[r.status]} label={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Drawer anchor="right" open={!!detail} onClose={close} PaperProps={{ sx: { width: { xs: '100%', sm: 460 }, p: 2 } }}>
        {detail && (
          <Box data-testid="approval-detail" data-help-section="approvals.inbox.detail">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">{detail.request.subjectLabel} {detail.request.subjectNumber ?? ''}</Typography>
              <IconButton onClick={close} aria-label="Close"><CloseIcon /></IconButton>
            </Box>
            <Chip size="small" color={STATUS_COLOR[detail.request.status]} label={detail.request.status} sx={{ mb: 1 }} />
            <Typography variant="body2">Submitted by {detail.request.submittedByName ?? '—'} on {formatDate(detail.request.submittedAt)}</Typography>
            {detail.request.workflowName && <Typography variant="caption" color="text.secondary">Workflow: {detail.request.workflowName}</Typography>}
            {detail.summary && (
              <Paper variant="outlined" sx={{ p: 1.5, my: 2 }}>
                <Typography variant="body2" data-testid="approval-detail-party">
                  {CASH_SUBJECTS.has(detail.request.subjectType) ? 'Category' : 'Party'}: {detail.summary.party ?? '—'}
                </Typography>
                <Typography variant="body2">Amount: {money(detail.summary.amount)} {detail.summary.currency ?? ''}</Typography>
                {detail.summary.date && <Typography variant="body2">Date: {formatDate(detail.summary.date)}</Typography>}
                {detail.summary.description && <Typography variant="body2">{detail.summary.description}</Typography>}
                {detail.summary.lines.map((l, i) => (
                  <Typography key={i} variant="caption" sx={{ display: 'block' }}>{l.description} · {l.quantity ?? ''} · {money(l.amount)}</Typography>
                ))}
                {detail.summary.deepLink && (
                  <Button data-help="approvals.inbox.openDocument" size="small" sx={{ mt: 1 }} onClick={() => navigate(detail.summary!.deepLink!)}>Open document</Button>
                )}
              </Paper>
            )}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Steps</Typography>
            {detail.steps.map(s => (
              <Box key={s.seq} sx={{ py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} data-testid={`approval-step-${s.seq}`}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.seq}. {s.approverLabel}</Typography>
                  <Chip size="small" color={STEP_COLOR[s.status]} label={s.status} />
                  {s.authorityLimit != null && <Typography variant="caption">up to {money(s.authorityLimit)}</Typography>}
                </Box>
                {s.decidedByName && <Typography variant="caption" color="text.secondary">{s.decidedByName}{s.decidedAt ? ` · ${formatDate(s.decidedAt)}` : ''}</Typography>}
                {s.comment && <Typography variant="body2">“{s.comment}”</Typography>}
                {s.skipReason && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{s.skipReason}</Typography>}
              </Box>
            ))}
            {detail.canDecide && (
              <Box sx={{ mt: 2 }}>
                <TextField data-help="approvals.inbox.comment" size="small" multiline minRows={2} label="Comment (required to reject)" value={comment}
                  onChange={e => setComment(e.target.value)} inputProps={{ maxLength: 1000 }} sx={{ width: '100%' }} />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button data-help="approvals.inbox.approve" variant="contained" color="success" disabled={busy} onClick={() => decide(true)}>Approve</Button>
                  <Button data-help="approvals.inbox.reject" variant="outlined" color="error" disabled={busy} onClick={() => decide(false)}>Reject</Button>
                </Box>
              </Box>
            )}
            {detail.canCancel && (
              <Button data-help="approvals.inbox.withdraw" sx={{ mt: 2 }} disabled={busy} onClick={cancel}>Withdraw request</Button>
            )}
            {detail.canAdminCancel && (
              <Button data-help="approvals.inbox.adminCancel" sx={{ mt: 2 }} color="warning" disabled={busy} onClick={adminCancel}>Cancel request (administrator)</Button>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default ApprovalsInbox;
